export enum UserRole {
  // Core Trading Roles
  BUYER_RFQ_TRADER = 'BUYER_RFQ_TRADER',
  SELLER_RFQ_TRADER = 'SELLER_RFQ_TRADER',
  BI_DIRECTIONAL_RFQ_TRADER = 'BI_DIRECTIONAL_RFQ_TRADER',
  BUYER_AUTO_EX_TRADER = 'BUYER_AUTO_EX_TRADER',
  SELLER_AUTO_EX_TRADER = 'SELLER_AUTO_EX_TRADER',
  BI_DIRECTIONAL_AUTO_EX_TRADER = 'BI_DIRECTIONAL_AUTO_EX_TRADER',

  // Company Admin Roles
  BUYER_COMPANY_ADMIN = 'BUYER_COMPANY_ADMIN',
  SELLER_COMPANY_ADMIN = 'SELLER_COMPANY_ADMIN',
  BI_DIRECTIONAL_COMPANY_ADMIN = 'BI_DIRECTIONAL_COMPANY_ADMIN',
  ADVISOR_COMPANY_ADMIN = 'ADVISOR_COMPANY_ADMIN',

  // Legal Roles
  BUYER_LEGAL = 'BUYER_LEGAL',
  SELLER_LEGAL = 'SELLER_LEGAL',
  BI_DIRECTIONAL_LEGAL = 'BI_DIRECTIONAL_LEGAL',
  AUTHORIZED_SIGNATORY = 'AUTHORIZED_SIGNATORY',

  // Advisor Roles
  BUYER_TRADER_ADVISOR = 'BUYER_TRADER_ADVISOR',
  SELLER_TRADER_ADVISOR = 'SELLER_TRADER_ADVISOR',
  BI_DIRECTIONAL_TRADER_ADVISOR = 'BI_DIRECTIONAL_TRADER_ADVISOR',

  // Execution Trader Roles — exclusively gate final trade execution
  BUYER_EXECUTION_TRADER = 'BUYER_EXECUTION_TRADER',
  SELLER_EXECUTION_TRADER = 'SELLER_EXECUTION_TRADER',
  BI_DIRECTIONAL_EXECUTION_TRADER = 'BI_DIRECTIONAL_EXECUTION_TRADER',

  // View only Roles
  VIEW_ONLY_BUYER = 'VIEW_ONLY_BUYER',
  VIEW_ONLY_SELLER = 'VIEW_ONLY_SELLER',
  VIEW_ONLY_BI_DIRECTIONAL = 'VIEW_ONLY_BI_DIRECTIONAL',
  VIEW_ONLY_BUYER_ADVISOR = 'VIEW_ONLY_BUYER_ADVISOR',
  VIEW_ONLY_SELLER_ADVISOR = 'VIEW_ONLY_SELLER_ADVISOR',
  VIEW_ONLY_BI_DIRECTIONAL_ADVISOR = 'VIEW_ONLY_BI_DIRECTIONAL_ADVISOR',

  // Default role for Advisor-company users (no privileges)
  ADVISOR_USER = 'ADVISOR_USER',
}

/**
 * Granular privileges resolved by the backend per company and surfaced on the
 * `/me` payload (`UserCompany.privileges`). Mirrors the backend `Privilege` enum
 * (com.cleantrade.core.enums.Privilege) — the source of truth is
 * `UserRolePrivilegeCatalog`. Used to gate action affordances in the UI so the
 * frontend only shows what the backend will authorize.
 */
export enum Privilege {
  // User
  GET_USER = 'GET_USER',
  GET_USERS = 'GET_USERS',
  CREATE_USER = 'CREATE_USER',
  UPDATE_USER = 'UPDATE_USER',
  UPDATE_USER_STATUS = 'UPDATE_USER_STATUS',
  DELETE_USER = 'DELETE_USER',

  // Company
  GET_COMPANY = 'GET_COMPANY',
  GET_COMPANY_LEGAL_ENTITIES = 'GET_COMPANY_LEGAL_ENTITIES',
  CREATE_COMPANY = 'CREATE_COMPANY',
  UPDATE_COMPANY = 'UPDATE_COMPANY',
  DELETE_COMPANY = 'DELETE_COMPANY',
  GET_USER_ROLES_BY_COMPANY = 'GET_USER_ROLES_BY_COMPANY',
  GET_USER_MEMBERSHIPS = 'GET_USER_MEMBERSHIPS',
  SEARCH_COMPANIES = 'SEARCH_COMPANIES',

  // Project
  GET_PROJECT = 'GET_PROJECT',
  GET_PROJECTS = 'GET_PROJECTS',
  CREATE_PROJECT = 'CREATE_PROJECT',
  UPDATE_PROJECT = 'UPDATE_PROJECT',
  DELETE_PROJECT = 'DELETE_PROJECT',

  // Membership
  GET_MEMBERSHIP = 'GET_MEMBERSHIP',
  CREATE_MEMBERSHIP = 'CREATE_MEMBERSHIP',
  UPDATE_MEMBERSHIP = 'UPDATE_MEMBERSHIP',
  DELETE_MEMBERSHIP = 'DELETE_MEMBERSHIP',

  // Offer
  GET_OFFER = 'GET_OFFER',
  GET_OFFERS = 'GET_OFFERS',
  CREATE_OFFER = 'CREATE_OFFER',
  UPDATE_OFFER = 'UPDATE_OFFER',
  DELETE_OFFER = 'DELETE_OFFER',

  // Bid
  GET_BID = 'GET_BID',
  GET_BIDS = 'GET_BIDS',
  CREATE_BID = 'CREATE_BID',
  UPDATE_BID = 'UPDATE_BID',
  DELETE_BID = 'DELETE_BID',

  // Negotiation
  CREATE_NEGOTIATION = 'CREATE_NEGOTIATION',
  DELETE_NEGOTIATION = 'DELETE_NEGOTIATION',
  GET_NEGOTIATION = 'GET_NEGOTIATION',
  GET_NEGOTIATIONS = 'GET_NEGOTIATIONS',
  ACCEPT_NEGOTIATION = 'ACCEPT_NEGOTIATION',
  COUNTER_NEGOTIATION = 'COUNTER_NEGOTIATION',
  CONFIRM_NEGOTIATION = 'CONFIRM_NEGOTIATION',
  REJECT_NEGOTIATION = 'REJECT_NEGOTIATION',
  RETURN_NEGOTIATION = 'RETURN_NEGOTIATION',

  // Contracting
  VIEW_CONTRACTING = 'VIEW_CONTRACTING',
  DOWNLOAD_DOCUMENTS = 'DOWNLOAD_DOCUMENTS',
  UPLOAD_DOCUMENTS = 'UPLOAD_DOCUMENTS',
  AGREE_DOCUMENTS = 'AGREE_DOCUMENTS',
  SIGN_DOCUMENTS = 'SIGN_DOCUMENTS',
  REJECT_DELETE_DOCUMENTS = 'REJECT_DELETE_DOCUMENTS',
  CHANGE_DOCUMENT_PACKAGE = 'CHANGE_DOCUMENT_PACKAGE',

  // Term Sheet
  AGREE_REJECT_TERM_SHEET = 'AGREE_REJECT_TERM_SHEET',

  // Execution
  EXECUTE_TRADE = 'EXECUTE_TRADE',
}

export enum Side {
  BUYER = 'BUYER',
  SELLER = 'SELLER',
}

export enum CompanyRole {
  BUYER_ONLY = 'BUYER_ONLY',
  SELLER_ONLY = 'SELLER_ONLY',
  BI_DIRECTIONAL = 'BI_DIRECTIONAL',
  ADVISOR = 'ADVISOR',
}

export enum CompanyCreditProfile {
  INVESTMENT_GRADE = 'INVESTMENT_GRADE',
  NON_INVESTMENT_GRADE = 'NON_INVESTMENT_GRADE',
}

export enum UserStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
}

export enum USER_TYPE {
  PLATFORM_ADMIN = 'Platform Admin',
  TECHNICAL_SUPPORT = 'Technical Support',
  USER = 'User',
}

export enum RegulatoryStatus {
  COMMODITY_TRADE_ADVISOR = 'COMMODITY_TRADE_ADVISOR',
  COMMODITY_POOL_OPERATOR = 'COMMODITY_POOL_OPERATOR',
  EXEMPT_NON_US_FIRM = 'EXEMPT_NON_US_FIRM',
  FUTURES_COMMISSIONS_MERCHANT = 'FUTURES_COMMISSIONS_MERCHANT',
  INTRODUCING_BROKER = 'INTRODUCING_BROKER',
  SWAP_DEALER = 'SWAP_DEALER',
  INVESTMENT_ADVISOR = 'INVESTMENT_ADVISOR',
  MAJOR_SWAP_PARTICIPANT = 'MAJOR_SWAP_PARTICIPANT',
  NO_FIRM_REGISTRATION = 'NO_FIRM_REGISTRATION',
  OTHER = 'OTHER',
}

export enum CompanyFormMode {
  ADD = 'add',
  EDIT = 'edit',
}

export enum RoutePath {
  DASHBOARD = '/',
  MARKET_ACTIVITY = '/marketactivity',
  MARKET_DEPTH = '/marketdepth',
  ADMIN = '/admin',
  ADMIN_USERS = '/admin/users',
  ADMIN_USERS_NEW = '/admin/users/new',
  ADMIN_ROLES = '/admin/roles',
  ADMIN_AUDIT = '/admin/audit',
  ADMIN_COMPANIES = '/admin/companies',
  ADMIN_COMPANY = '/admin/company',
  ADMIN_COMPANY_NEW = '/admin/company/new',
  ADMIN_PACKAGES = '/admin/packages',
  ADMIN_FIELD_LIBRARY = '/admin/fieldlibrary',
  ADMIN_BLOCK_TRADE = '/admin/blocktrade',
  ADMIN_KEY_METRICS = '/admin/key-metrics',
  ADMIN_MARKET_REPORT = '/admin/market-report',
  SETTINGS = '/settings',
  SETTINGS_ACCOUNT = '/settings/account',
  SETTINGS_COMPANY = '/settings/company',
  SETTINGS_ALERTS = '/settings/alerts',
  PROJECT = '/project',
  TRANSACTIONS = '/transactions',
  ACTIONS = '/actions',
  SIGNING_COMPLETE = '/contracting/signing-complete',
  UNSUBSCRIBE = '/unsubscribe',
}

// ISO enum
export enum ISO {
  CAISO = 'CAISO',
  ERCOT = 'ERCOT',
  ISONE = 'ISONE',
  MISO = 'MISO',
  NYISO = 'NYISO',
  PJM = 'PJM',
  SPP = 'SPP',
}

// Technology enum
export enum TECHNOLOGY_DISPLAY_NAME {
  WIND = 'Wind',
  SOLAR = 'Solar',
  WIND_AND_SOLAR = 'Wind And Solar',
  BATTERY = 'Battery',
}

export enum TECHNOLOGY_TYPE {
  WIND = 'WIND',
  SOLAR = 'SOLAR',
  WIND_AND_SOLAR = 'WIND_AND_SOLAR',
  BATTERY = 'BATTERY',
}

/**
 * How a battery project is sited. Mirrors the backend `BatteryConfiguration` enum
 * (com.cleantrade.core.enums.BatteryConfiguration). Every value except `STANDALONE`
 * is co-located with a generator. Display labels come from
 * `/reference/battery-configurations`.
 */
export enum BatteryConfiguration {
  STANDALONE = 'STANDALONE',
  CO_LOCATED_AC_COUPLED = 'CO_LOCATED_AC_COUPLED',
  CO_LOCATED_DC_COUPLED = 'CO_LOCATED_DC_COUPLED',
  CO_LOCATED_SURPLUS_INTERCONNECTION = 'CO_LOCATED_SURPLUS_INTERCONNECTION',
}

/**
 * What a battery project charges from. Mirrors the backend `ChargingSource` enum.
 * A `STANDALONE` battery must charge from `GRID`. Display labels come from
 * `/reference/charging-sources`.
 */
export enum ChargingSource {
  GRID = 'GRID',
  PAIRED = 'PAIRED',
  BOTH = 'BOTH',
}

/**
 * Limits on when or how much a battery project may charge. Mirrors the backend
 * `ChargeRestriction` enum. `NONE` excludes the other two. The backend derives the
 * restrictions from the stored window and limit values, so the form never sends
 * them. Display labels come from `/reference/charge-restrictions`.
 */
export enum ChargeRestriction {
  MW_LIMIT = 'MW_LIMIT',
  TIME_OF_DAY_WINDOW = 'TIME_OF_DAY_WINDOW',
  NONE = 'NONE',
}

/**
 * The cell chemistry of a battery project. Mirrors the backend `StorageTechnology`
 * enum. Display labels come from `/reference/storage-technologies`.
 */
export enum StorageTechnology {
  LITHIUM_ION_LFP = 'LITHIUM_ION_LFP',
  LITHIUM_ION_NMC = 'LITHIUM_ION_NMC',
  FLOW = 'FLOW',
  OTHER = 'OTHER',
}

export enum INTERCONNECTION_VOLTAGES {
  KV_69 = 'KV_69',
  KV_138 = 'KV_138',
  KV_230 = 'KV_230',
  KV_345 = 'KV_345',
  KV_500 = 'KV_500',
}

export enum BID_TECHNOLOGY_TYPE {
  WIND = 'WIND',
  SOLAR = 'SOLAR',
  WIND_AND_SOLAR = 'WIND_AND_SOLAR',
  BATTERY = 'BATTERY',
}

export enum OFFER_STATUS {
  ACTIVE = 'ACTIVE',
  TRADE_PENDING = 'TRADE_PENDING',
  EXISTING_INTEREST = 'EXISTING_INTEREST',
  EXECUTED = 'EXECUTED',
  DELISTED = 'DELISTED',
  DELETED = 'DELETED',
  AWAITING_KEY_METRICS = 'AWAITING_KEY_METRICS',
  INSUFFICIENT_DATA = 'INSUFFICIENT_DATA',
  INTERNAL_PORTFOLIO = 'INTERNAL_PORTFOLIO',
}

export enum BID_STATUS {
  ACTIVE = 'ACTIVE',
  TRADE_PENDING = 'TRADE_PENDING',
  EXISTING_INTEREST = 'EXISTING_INTEREST',
  EXECUTED = 'EXECUTED',
  DELISTED = 'DELISTED',
  DELETED = 'DELETED',
}

/**
 * The statuses a negotiation projects onto a bid or an offer, with their display labels. The two
 * enums share these values. Neither owner can select one - the status options are ACTIVE and
 * DELISTED - so an edit form shows it as the current, unselectable value and sends it back
 * unchanged unless the owner delists. See RME-1250.
 */
export const NEGOTIATION_STATUS_LABELS: Readonly<Record<string, string>> = {
  [OFFER_STATUS.EXISTING_INTEREST]: 'Existing Interest',
  [OFFER_STATUS.TRADE_PENDING]: 'Trade Pending',
};

export const OFFER_ACTIVE_STATUSES = [
  OFFER_STATUS.ACTIVE,
  OFFER_STATUS.TRADE_PENDING,
  OFFER_STATUS.EXISTING_INTEREST,
];

export const OFFER_PENDING_STATUSES = [OFFER_STATUS.AWAITING_KEY_METRICS];

export const OFFER_INACTIVE_STATUSES = [
  OFFER_STATUS.DELISTED,
  OFFER_STATUS.INSUFFICIENT_DATA,
  OFFER_STATUS.EXECUTED,
];

export const BID_ACTIVE_STATUSES = [
  BID_STATUS.ACTIVE,
  BID_STATUS.TRADE_PENDING,
  BID_STATUS.EXISTING_INTEREST,
];
export const BID_INACTIVE_STATUSES = [
  BID_STATUS.EXECUTED,
  BID_STATUS.DELISTED,
  BID_STATUS.DELETED,
];

export enum PROJECT_OPERATIONAL_STATUS {
  NEW_BUILD = 'NEW_BUILD',
  OPERATIONAL = 'OPERATIONAL',
}

export enum DisplayValue {
  NEVER = 'Never',
  NONE = 'None',
  YES = 'Yes',
  NO = 'No',
}

export enum COMPLIANCE_PERMIT_STATUS {
  NOT_SUBMITTED = 'Not Submitted',
  SUBMITTED = 'Submitted',
  APPROVED = 'Approved',
  NOT_APPLICABLE = 'Not Applicable',
}

export enum COMPLIANCE_DOCUMENT_STATUS {
  WORK_IN_PROGRESS = 'Work in Progress',
  DRAFT_AVAILABLE = 'Draft Available',
  EXECUTED = 'Executed',
  NOT_APPLICABLE = 'Not Applicable',
}

export enum PRODUCT_TYPE {
  VIRTUAL_PPA = 'VIRTUAL_PPA',
  PHYSICAL_PPA = 'PHYSICAL_PPA',
  RECS = 'RECS',
  FULL_TOLL = 'FULL_TOLL',
  TB_SWAP = 'TB_SWAP',
  FLOOR_UPSIDE_SHARE = 'FLOOR_UPSIDE_SHARE',
}

/**
 * The unit of a price, as the API returns it in `priceUnit`. Mirrors the backend `PriceUnit` enum
 * (com.cleantrade.core.enums.PriceUnit). Holds only the ids: the frontend never maps a product
 * type to a unit itself. Display labels come from `/reference/price-units`.
 */
export enum PriceUnit {
  USD_PER_MWH = 'USD_PER_MWH',
  USD_PER_KW_MONTH = 'USD_PER_KW_MONTH',
}

export enum OFFERS_STATUS_FILTER {
  ACTIVE = 'ACTIVE',
  PENDING = 'PENDING',
  INACTIVE = 'INACTIVE',
}

export enum BIDS_STATUS_FILTER {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export const NONE = 'NONE';
export const NOT_APPLICABLE = 'NOT_APPLICABLE';

export enum REC_PROGRAM {
  GREEN_E_REC = 'GREEN_E_REC',
  COMPLIANCE_REC = 'COMPLIANCE_REC',
  NONE = 'NONE',
  ANY = 'ANY',
}

export enum COMPANY_CREDIT_PROFILE {
  LETTER_OF_CREDIT = 'LETTER_OF_CREDIT',
  OTHER = 'OTHER',
  INVESTMENT_GRADE = 'INVESTMENT_GRADE',
  NON_INVESTMENT_GRADE = 'NON_INVESTMENT_GRADE',
  PARENTAL_GUARANTEE_FROM_INVESTMENT_GRADE_ENTITY = 'PARENTAL_GUARANTEE_FROM_INVESTMENT_GRADE_ENTITY',
  PARENTAL_GUARANTEE_FROM_NON_INVESTMENT_GRADE_ENTITY = 'PARENTAL_GUARANTEE_FROM_NON_INVESTMENT_GRADE_ENTITY',
  PROJECT_LIEN = 'PROJECT_LIEN',
}

export enum REC_DELIVERY_METHODOLOGY {
  NONE = 'NONE',
}

export enum APPLICABLE_TRACKING_SYSTEM {
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export enum NEGATIVE_PRICING_METHOD {
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export enum AUDIT_TYPE {
  OFFER = 'Offer',
  BID = 'Bid',
  NEGOTIATION = 'Negotiation',
}

export enum OfferFormMode {
  ADD = 'add',
  EDIT = 'edit',
  COPY = 'copy',
  PROPOSE = 'propose',
}

export enum BidFormMode {
  ADD = 'add',
  EDIT = 'edit',
  COPY = 'copy',
  PROPOSE = 'propose',
}

export enum MARKET_DEPTH_SIDE {
  BID = 'BID',
  OFFER = 'OFFER',
}

export enum PositionType {
  BID = 'BID',
  OFFER = 'OFFER',
}

export enum MarketActivityEventType {
  NEW_POSITION = 'NEW_POSITION',
  POSITION_EDITED = 'POSITION_EDITED',
  STATUS_CHANGED = 'STATUS_CHANGED',
  POSITION_EXECUTED = 'POSITION_EXECUTED',
}

/**
 * What a Market Activity Feed alert subscription watches. Name and values
 * match the backend enum (`com.cleantrade.core.enums.MarketAlertMode`), which
 * also names the feed endpoint's `syncMode` parameter — one vocabulary across
 * the row, the request and the query.
 *
 * There is deliberately no `GLOBAL` mode — an unfiltered "All Activity" alert
 * is a `MANUAL` subscription with an empty filter set.
 */
export enum MarketAlertMode {
  /** Watches a filter set snapshotted when the subscription was created. */
  MANUAL = 'MANUAL',
  /** Watches counter-positions matching any of the company's active positions. */
  PORTFOLIO_SYNC = 'PORTFOLIO_SYNC',
  /** Watches counter-positions matching one specific position. */
  POSITION_SYNC = 'POSITION_SYNC',
}

export enum CONTRACT_TYPE {
  NEW_BUILD_VPPA = 'NEW_BUILD_VPPA',
  POST_BUILD_VPPA = 'POST_BUILD_VPPA',
  NEW_BUILD_PPA = 'NEW_BUILD_PPA',
  POST_BUILD_PPA = 'POST_BUILD_PPA',
  REC = 'REC',
  NEW_BUILD_TOLL = 'NEW_BUILD_TOLL',
  POST_BUILD_TOLL = 'POST_BUILD_TOLL',
  NEW_BUILD_TB_SWAP = 'NEW_BUILD_TB_SWAP',
  POST_BUILD_TB_SWAP = 'POST_BUILD_TB_SWAP',
  NEW_BUILD_FLOOR = 'NEW_BUILD_FLOOR',
  POST_BUILD_FLOOR = 'POST_BUILD_FLOOR',
}

export enum GROUPED_CONTRACT_TYPE {
  NEW_BUILD = 'NEW_BUILD',
  POST_BUILD = 'POST_BUILD',
  REC = 'REC',
}

export const GROUPED_CONTRACT_TYPE_MAPPING: Record<
  GROUPED_CONTRACT_TYPE,
  CONTRACT_TYPE[]
> = {
  [GROUPED_CONTRACT_TYPE.NEW_BUILD]: [
    CONTRACT_TYPE.NEW_BUILD_VPPA,
    CONTRACT_TYPE.NEW_BUILD_PPA,
  ],
  [GROUPED_CONTRACT_TYPE.POST_BUILD]: [
    CONTRACT_TYPE.POST_BUILD_VPPA,
    CONTRACT_TYPE.POST_BUILD_PPA,
  ],
  [GROUPED_CONTRACT_TYPE.REC]: [CONTRACT_TYPE.REC],
};

/**
 * The build stage of every contract type, BESS included. A `Record` so that a new
 * contract type fails to compile until it has a stage.
 *
 * Not the same as `GROUPED_CONTRACT_TYPE_MAPPING`, which still holds only the
 * pre-BESS members: the grouped filter collapses a group only when every member is
 * selected, so adding members would stop saved alert filters displaying as
 * "New Build". See the BESS launch gate wiki page.
 */
export const CONTRACT_TYPE_BUILD_STAGE: Record<
  CONTRACT_TYPE,
  GROUPED_CONTRACT_TYPE
> = {
  [CONTRACT_TYPE.NEW_BUILD_VPPA]: GROUPED_CONTRACT_TYPE.NEW_BUILD,
  [CONTRACT_TYPE.POST_BUILD_VPPA]: GROUPED_CONTRACT_TYPE.POST_BUILD,
  [CONTRACT_TYPE.NEW_BUILD_PPA]: GROUPED_CONTRACT_TYPE.NEW_BUILD,
  [CONTRACT_TYPE.POST_BUILD_PPA]: GROUPED_CONTRACT_TYPE.POST_BUILD,
  [CONTRACT_TYPE.REC]: GROUPED_CONTRACT_TYPE.REC,
  [CONTRACT_TYPE.NEW_BUILD_TOLL]: GROUPED_CONTRACT_TYPE.NEW_BUILD,
  [CONTRACT_TYPE.POST_BUILD_TOLL]: GROUPED_CONTRACT_TYPE.POST_BUILD,
  [CONTRACT_TYPE.NEW_BUILD_TB_SWAP]: GROUPED_CONTRACT_TYPE.NEW_BUILD,
  [CONTRACT_TYPE.POST_BUILD_TB_SWAP]: GROUPED_CONTRACT_TYPE.POST_BUILD,
  [CONTRACT_TYPE.NEW_BUILD_FLOOR]: GROUPED_CONTRACT_TYPE.NEW_BUILD,
  [CONTRACT_TYPE.POST_BUILD_FLOOR]: GROUPED_CONTRACT_TYPE.POST_BUILD,
};

export const GROUPED_CONTRACT_TYPE_OPTIONS = [
  { id: GROUPED_CONTRACT_TYPE.NEW_BUILD, name: 'New Build' },
  { id: GROUPED_CONTRACT_TYPE.POST_BUILD, name: 'Post Build' },
  { id: GROUPED_CONTRACT_TYPE.REC, name: 'REC' },
];

export enum COUNTRY {
  USA = 'United States',
}

export enum FORECAST_FILTER {
  FORECAST = 'Forecast',
  HISTORICAL = 'Historical',
}

export enum STATS_FILTER {
  P05 = 'P05',
  MEAN = 'Mean',
  P95 = 'P95',
}

export enum PERIODICITY_FILTER {
  MONTHLY = 'Monthly',
  QUARTERLY = 'Quarterly',
  YEARLY = 'Yearly',
}

export enum CHART_INTERVAL {
  ONE_MONTH = 'ONE_MONTH',
  THREE_MONTHS = 'THREE_MONTHS',
  ONE_YEAR = 'ONE_YEAR',
}
export enum CHART_TYPE {
  CFE_PERCENTAGE = 'CFE_PERCENTAGE',
  NODAL_BASIS = 'NODAL_BASIS',
  GENERATION = 'GENERATION',
  CARBON_REDUCTION = 'CARBON_REDUCTION',
  FLOATING_PRICE = 'FLOATING_PRICE',
  SETTLEMENT_FORECAST = 'SETTLEMENT_FORECAST',
  IMPLIED_CARBON_PRICE = 'IMPLIED_CARBON_PRICE',
}

export enum FORECAST_SCENARIO {
  BASELINE = 'Baseline',
  HIGH_GAS = 'High Gas',
  LOW_GAS = 'Low Gas',
}

export enum FORECAST_SCENARIO_ID {
  BASELINE = 'baseline',
  HIGH_GAS = 'highGas',
  LOW_GAS = 'lowGas',
}

export const chartIntervalMap: Record<PERIODICITY_FILTER, CHART_INTERVAL> = {
  [PERIODICITY_FILTER.MONTHLY]: CHART_INTERVAL.ONE_MONTH,
  [PERIODICITY_FILTER.QUARTERLY]: CHART_INTERVAL.THREE_MONTHS,
  [PERIODICITY_FILTER.YEARLY]: CHART_INTERVAL.ONE_YEAR,
};

export enum NEGOTIATION_STATUSES {
  // A seller has staged an offer against a bid but has not sent the proposal. The negotiation
  // exists and is visible to the seller only; the buyer is not told about it until it is sent.
  STAGED = 'STAGED',
  IN_PROGRESS = 'IN_PROGRESS',
  REQUIRES_CONFIRMATION = 'REQUIRES_CONFIRMATION',
  CONFIRMED_AWAITING_BUYER = 'CONFIRMED_AWAITING_BUYER',
  CONFIRMED_AWAITING_SELLER = 'CONFIRMED_AWAITING_SELLER',
  REJECTED = 'REJECTED',
  CONTRACTING = 'CONTRACTING',
  // Contracting is complete, but the final buyer's fraction differs from the offer's, so CleanSight
  // is minting a forecast at the final terms (RME-1368). Nobody can execute until it arrives; the
  // backend then moves the negotiation into the execution window.
  PENDING_EXECUTION_METRICS = 'PENDING_EXECUTION_METRICS',
  PENDING_EXECUTION_TIMER = 'PENDING_EXECUTION_TIMER',
  PENDING_EXECUTION = 'PENDING_EXECUTION',
  EXECUTED_AWAITING_BUYER = 'EXECUTED_AWAITING_BUYER',
  EXECUTED_AWAITING_SELLER = 'EXECUTED_AWAITING_SELLER',
  EXECUTED = 'EXECUTED',
  EXPIRED = 'EXPIRED',
  DELETED = 'DELETED',
  TRADED_AWAY = 'TRADED_AWAY',
}

export const ACTIVE_NEGOTIATION_STATUSES = [
  // Staged drafts are only ever fetched because they are listed here - both TradingActivity and
  // TransactionsDrawer send this array as an explicit status filter.
  // PENDING_EXECUTION_METRICS joins this list in RME-1368, with the backend enum value: the
  // backend refuses a filter value it does not know, which would empty every active list.
  NEGOTIATION_STATUSES.STAGED,
  NEGOTIATION_STATUSES.IN_PROGRESS,
  NEGOTIATION_STATUSES.REQUIRES_CONFIRMATION,
  NEGOTIATION_STATUSES.CONFIRMED_AWAITING_BUYER,
  NEGOTIATION_STATUSES.EXECUTED_AWAITING_BUYER,
  NEGOTIATION_STATUSES.CONTRACTING,
  NEGOTIATION_STATUSES.CONFIRMED_AWAITING_SELLER,
  NEGOTIATION_STATUSES.EXECUTED_AWAITING_SELLER,
  NEGOTIATION_STATUSES.PENDING_EXECUTION,
  NEGOTIATION_STATUSES.PENDING_EXECUTION_TIMER,
  NEGOTIATION_STATUSES.EXPIRED,
];

export const EXECUTED_NEGOTIATION_STATUSES = [NEGOTIATION_STATUSES.EXECUTED];

export const TERMINATED_NEGOTIATION_STATUSES = [
  NEGOTIATION_STATUSES.TRADED_AWAY,
  NEGOTIATION_STATUSES.REJECTED,
  NEGOTIATION_STATUSES.DELETED,
];

/**
 * Statuses a negotiation can only reach by one side accepting the other's proposal. From that
 * point on the negotiation record holds the agreed terms, and the transaction details table
 * renders them merged rather than per side. (RME-1013)
 *
 * STAGED and IN_PROGRESS are pre-acceptance. The four terminal statuses are excluded, and that is
 * a known limitation rather than an oversight: a negotiation can end either before or after
 * acceptance, and nothing on the DTO separates the two — acceptance leaves no timestamp, and
 * buyerConfirmedAt/sellerConfirmedAt are both unset for a rejection straight out of
 * REQUIRES_CONFIRMATION. So a negotiation rejected after acceptance falls back to the per-side
 * columns. Closing that needs an acceptance marker plus a decision on what a dead negotiation
 * should display, which is a product call, not display logic.
 */
export const AGREED_TERMS_NEGOTIATION_STATUSES: ReadonlySet<NEGOTIATION_STATUSES> =
  new Set([
    NEGOTIATION_STATUSES.REQUIRES_CONFIRMATION,
    NEGOTIATION_STATUSES.CONFIRMED_AWAITING_BUYER,
    NEGOTIATION_STATUSES.CONFIRMED_AWAITING_SELLER,
    NEGOTIATION_STATUSES.CONTRACTING,
    NEGOTIATION_STATUSES.PENDING_EXECUTION_METRICS,
    NEGOTIATION_STATUSES.PENDING_EXECUTION_TIMER,
    NEGOTIATION_STATUSES.PENDING_EXECUTION,
    NEGOTIATION_STATUSES.EXECUTED_AWAITING_BUYER,
    NEGOTIATION_STATUSES.EXECUTED_AWAITING_SELLER,
    NEGOTIATION_STATUSES.EXECUTED,
  ]);

export enum SettlementIndex {
  DAY_AHEAD = 'DAY_AHEAD',
  REAL_TIME = 'REAL_TIME',
}

export enum SettlementIndexName {
  DAY_AHEAD = 'Day Ahead',
  REAL_TIME = 'Real Time',
}

export enum TRADING_ACTIVITY {
  ACTION_REQUIRED = 'ACTION_REQUIRED',
  MONITORING = 'MONITORING',
  EXECUTED = 'EXECUTED',
}

export enum POSITION_TAGS {
  MATCHING_247 = 'matching247',
  BASIS_SHARING = 'basisSharing',
  BESS_OPTION = 'bessOption',
  CAPACITY_RIGHTS = 'capacityRights',
  COLOCATION_OPTION = 'colocationOption',
  ESCALATOR = 'escalator',
  NODAL_SETTLEMENT = 'nodalSettlement',
  PRICE_CAP = 'priceCap',
  PRICE_COLLAR = 'priceCollar',
  PRICE_FLOOR = 'priceFloor',
  REAL_TIME_DATA_ACCESS = 'realTimeDataAccess',
  SSA = 'ssa',
  SURETY_BOND = 'suretyBond',
  TAX_CREDIT_TRANSFERABILITY = 'taxCreditTransferability',
  UPFRONT_PAYMENT = 'upfrontPayment',
  UPSIDE_REVENUE_SHARE = 'upsideRevenueShare',
}

export const POSITION_TAGS_OPTIONS: Record<POSITION_TAGS, string> = {
  [POSITION_TAGS.MATCHING_247]: '24/7 Matching',
  [POSITION_TAGS.BASIS_SHARING]: 'Basis Sharing',
  [POSITION_TAGS.BESS_OPTION]: 'BESS Option',
  [POSITION_TAGS.CAPACITY_RIGHTS]: 'Capacity Rights',
  [POSITION_TAGS.COLOCATION_OPTION]: 'Colocation Option',
  [POSITION_TAGS.ESCALATOR]: 'Escalator',
  [POSITION_TAGS.NODAL_SETTLEMENT]: 'Nodal Settlement',
  [POSITION_TAGS.PRICE_CAP]: 'Price Cap',
  [POSITION_TAGS.PRICE_COLLAR]: 'Price Collar',
  [POSITION_TAGS.PRICE_FLOOR]: 'Price Floor',
  [POSITION_TAGS.REAL_TIME_DATA_ACCESS]: 'Real-Time Data Access',
  [POSITION_TAGS.SSA]: 'SSA',
  [POSITION_TAGS.SURETY_BOND]: 'Surety Bond',
  [POSITION_TAGS.TAX_CREDIT_TRANSFERABILITY]: 'Tax Credit Transferability',
  [POSITION_TAGS.UPFRONT_PAYMENT]: 'Upfront Payment',
  [POSITION_TAGS.UPSIDE_REVENUE_SHARE]: 'Upside/Revenue Share',
};

/**
 * Position tags available for selection when creating or editing bids/offers.
 * Excludes deprecated tags (e.g., 24/7 Matching) that still exist in the database
 * for historical records but should not be applied to new positions.
 */
export const SELECTABLE_POSITION_TAGS_OPTIONS: Partial<
  Record<POSITION_TAGS, string>
> = Object.fromEntries(
  Object.entries(POSITION_TAGS_OPTIONS).filter(
    ([key]) => key !== POSITION_TAGS.MATCHING_247
  )
);

export enum DASHBOARD_MOUNT_ACTION {
  NEW_BID = 'NEW_BID',
  NEW_OFFER = 'NEW_OFFER',
  NEW_PROJECT = 'NEW_PROJECT',
}

export enum SignatureIntent {
  MANUAL_PRESIGNED = 'MANUAL_PRESIGNED',
  DOCUSIGN = 'DOCUSIGN',
}

export enum ContractingDocumentStatus {
  DOCUMENT_CREATED = 'DOCUMENT_CREATED',
  SIGNATURE_REQUIRED_DOCUMENT_SHARED = 'SIGNATURE_REQUIRED_DOCUMENT_SHARED',
  NO_SIGNATURE_REQUIRED_DOCUMENT_SHARED = 'NO_SIGNATURE_REQUIRED_DOCUMENT_SHARED',
  SELLER_SIGNED = 'SELLER_SIGNED',
  BUYER_SIGNED = 'BUYER_SIGNED',
  SELLER_CONFIRMED = 'SELLER_CONFIRMED',
  BUYER_CONFIRMED = 'BUYER_CONFIRMED',
  DOCUMENT_FULLY_EXECUTED = 'DOCUMENT_FULLY_EXECUTED',
  DOCUMENT_WITHDREW_TERMINATED = 'DOCUMENT_WITHDREW_TERMINATED',
}

export enum PendingActionBucket {
  ACTION_REQUIRED = 'ACTION_REQUIRED',
  MONITORING = 'MONITORING',
}

export enum PendingActionCode {
  UPLOAD = 'UPLOAD',
  SIGN = 'SIGN',
  APPROVE = 'APPROVE',
  REVIEW_MIXED = 'REVIEW_MIXED',
  AWAITING_CPTY_SIGNATURE = 'AWAITING_CPTY_SIGNATURE',
  AWAITING_CPTY_APPROVAL = 'AWAITING_CPTY_APPROVAL',
  AWAITING_EXECUTION_HANDOFF = 'AWAITING_EXECUTION_HANDOFF',
  VIEW = 'VIEW',
}

// Alert email categories a user can turn off one at a time (mirrors the backend AlertCategory)
export enum AlertCategory {
  MARKET_ACTIVITY_FEED = 'MARKET_ACTIVITY_FEED',
  TRADING_ACTIVITY = 'TRADING_ACTIVITY',
  POSITION_MANAGEMENT = 'POSITION_MANAGEMENT',
}
